import { cn, PopoverContent, PopoverTrigger, Popover } from '@wellesley/ui'
import { CSSProperties, FC } from 'react'
import { TitleBlock, TitleBlockProps } from './TitleBlock'
import { useIsItemActive } from './hooks/useIsItemActive'
import { useSideBarVisible } from './hooks/useSideBarVisible'
import {
  usePopoverArrowPosition,
  PopoverArrow,
} from './hooks/usePopoverArrowPosition'
export { setActiveItem } from './slice'
import { SideBarPortal } from './SideBarPortal'

export type ToolbarItemProps = Omit<TitleBlockProps, 'isItemActive'> & {
  onHover?: (isHovered: boolean) => void
}

export const ITEM_WIDTH = 'min-w-80 w-80'
export const SIDEBAR_SPACE_WIDTH = 20

export const ToolbarItem: FC<ToolbarItemProps> = ({
  id,
  onHover,
  link,
  className,
  variant = 'square',
  children,
  onClick,
  ...props
}) => {
  const hasSideBar = Boolean(children)
  const { isItemActive } = useIsItemActive(id, link, hasSideBar)

  const { hoverRef, isSideBarOpen, isItemSticky, openSideBar } =
    useSideBarVisible({
      itemId: id,
      canStick: hasSideBar && isItemActive,
      onHover,
      onClick,
    })

  const { contentRef, arrowTop } = usePopoverArrowPosition(
    hoverRef,
    isSideBarOpen,
    isItemSticky,
  )

  const content = (
    <PopoverContent
      sideOffset={0}
      side="right"
      align="center"
      animate={false}
      className={cn(
        'relative m-none my-s ml-[--sideOffset] h-[calc(100vh-32px)] rounded-lg border border-slate-200  bg-white pt-none',
        isItemSticky
          ? 'shadow-none'
          : 'shadow-[0_0_12px_0_var(--alpha-black-10)]',
        ITEM_WIDTH,
      )}
      style={
        {
          '--sideOffset': `${SIDEBAR_SPACE_WIDTH}px`,
        } as CSSProperties
      }
    >
      <div ref={contentRef} className="relative h-full w-full">
        <div className="flex h-full flex-col items-center justify-between overflow-hidden overflow-y-auto rounded-lg">
          {children}
        </div>
        {!isItemSticky && <PopoverArrow top={arrowTop} />}
      </div>
    </PopoverContent>
  )

  return (
    <div
      ref={hoverRef}
      className={cn('cursor-pointer', className)}
      data-testid={id ? `toolbar-item-${id}` : undefined}
    >
      <Popover modal={false} open={hasSideBar && isSideBarOpen}>
        <PopoverTrigger>
          <TitleBlock
            id={id}
            link={link as undefined}
            variant={variant}
            onClick={children ? openSideBar : undefined}
            isItemActive={isItemActive}
            {...props}
          />
        </PopoverTrigger>
        {children &&
          (isItemSticky ? <SideBarPortal>{content}</SideBarPortal> : content)}
      </Popover>
    </div>
  )
}
